import React, { useState, JSX } from 'react';

interface JSONViewerProps {
  data: any;
  onSelect?: (path: string[], value: any, isCount?: boolean) => void;
  path?: string[];
  highlightPath?: string[];
}

export const JSONViewer: React.FC<JSONViewerProps> = ({ data, onSelect, path = [], highlightPath }) => {
  const [expandedKeys, setExpandedKeys] = useState(new Set<string>());

  const toggleExpand = (key: string) => {
    const newExpanded = new Set(expandedKeys);
    if (newExpanded.has(key)) {
      newExpanded.delete(key);
    } else {
      newExpanded.add(key);
    }
    setExpandedKeys(newExpanded);
  };

  const handleClick = (e: React.MouseEvent, value: any, isCount: boolean = false) => {
    e.stopPropagation();
    if (onSelect) {
      onSelect(path, value, isCount);
    }
  };

  const handleCountClick = (e: React.MouseEvent, value: number) => {
    e.stopPropagation();
    if (onSelect) {
      onSelect(path, value, true);
    }
  };

  const getItemCount = (value: any): number => {
    if (Array.isArray(value)) {
      return value.length;
    } else if (typeof value === 'object' && value !== null) {
      return Object.keys(value).length;
    }
    return 0;
  };

  const formatKey = (key: string): string => {
    return key
      .split(/(?=[A-Z])|_/)
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  // Helper to check if current path matches highlight path
  const isHighlighted = (currentPath: string[]) => {
    if (!highlightPath) return false;
    return currentPath.join('.') === highlightPath.join('.');
  };

  const getPathId = (currentPath: string[]) => {
    return `json-path-${currentPath.join('-')}`;
  };

  // Automatically expand parents of highlighted element
  React.useEffect(() => {
    if (highlightPath) {
      const newExpanded = new Set(expandedKeys);
      for (let i = 1; i <= highlightPath.length; i++) {
        newExpanded.add(highlightPath.slice(0, i).join('.'));
      }
      setExpandedKeys(newExpanded);
      
      // Scroll to element after a short delay to ensure rendering
      setTimeout(() => {
        const element = document.getElementById(getPathId(highlightPath));
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          element.classList.add('bg-yellow-100');
          setTimeout(() => element.classList.remove('bg-yellow-100'), 2000);
        }
      }, 100);
    }
  }, [highlightPath]);

  const renderValue = (value: any, currentPath: string[] = path): JSX.Element => {
    const pathId = getPathId(currentPath);
    const isCurrentHighlighted = isHighlighted(currentPath);

    if (value === null) {
      return <span className="text-gray-500">null</span>;
    }
    if (value === undefined) {
      return <span className="text-gray-500">undefined</span>;
    }
    if (typeof value === 'boolean') {
      return (
        <span 
          id={pathId}
          className={`text-purple-600 cursor-pointer hover:underline ${isCurrentHighlighted ? 'bg-yellow-100' : ''}`}
          onClick={(e) => handleClick(e, value)}
        >
          {value.toString()}
        </span>
      );
    }
    if (typeof value === 'number') {
      return (
        <span 
          id={pathId}
          className={`text-blue-600 cursor-pointer hover:underline ${isCurrentHighlighted ? 'bg-yellow-100' : ''}`}
          onClick={(e) => handleClick(e, value)}
        >
          {value}
        </span>
      );
    }
    if (typeof value === 'string') {
      return (
        <span 
          id={pathId}
          className={`text-green-600 cursor-pointer hover:underline ${isCurrentHighlighted ? 'bg-yellow-100' : ''}`}
          onClick={(e) => handleClick(e, value)}
        >
          "{value}"
        </span>
      );
    }

    if (Array.isArray(value)) {
      if (value.length === 0) {
        return <span className="text-gray-500">[]</span>;
      }
      const isExpanded = expandedKeys.has(currentPath.join('.'));

      return (
        <div className="ml-4" id={pathId}>
          <div
            className={`flex items-center cursor-pointer hover:bg-gray-50 -ml-4 px-4 py-1 ${isCurrentHighlighted ? 'bg-yellow-100' : ''}`}
            onClick={(e) => {
              e.stopPropagation();
              toggleExpand(currentPath.join('.'));
            }}
          >
            <span className="text-gray-500 mr-2">{isExpanded ? '▼' : '▶'}</span>
            <span className="text-gray-600">[{value.length} items]</span>
            <span 
              className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full cursor-pointer hover:bg-blue-200"
              onClick={(e) => {
                e.stopPropagation();
                if (onSelect) {
                  onSelect(currentPath, value.length, true);
                }
              }}
              title="Click to add count as metric"
            >
              Count: {value.length}
            </span>
          </div>
          {isExpanded && (
            <div className="ml-4 border-l border-gray-200 pl-4">
              {value.map((item, index) => (
                <div key={index} className="my-1">
                  {renderValue(item, [...currentPath, index.toString()])}
                </div>
              ))}
            </div>
          )}
        </div>
      );
    }

    if (typeof value === 'object') {
      const entries = Object.entries(value);
      if (entries.length === 0) {
        return <span className="text-gray-500">{'{}'}</span>;
      }

      return (
        <div className="ml-4" id={pathId}>
          {entries.map(([key, val]) => {
            const newPath = [...currentPath, key];
            const isExpandable = typeof val === 'object' && val !== null;
            const isExpanded = expandedKeys.has(newPath.join('.'));
            const itemCount = isExpandable ? getItemCount(val) : 0;
            const isItemHighlighted = isHighlighted(newPath);

            return (
              <div key={key} className="my-1">
                <div
                  className={`flex items-start ${isExpandable ? 'cursor-pointer hover:bg-gray-50 -ml-4 px-4 py-1' : ''} ${isItemHighlighted ? 'bg-yellow-100' : ''}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (isExpandable) toggleExpand(newPath.join('.'));
                  }}
                >
                  {isExpandable && (
                    <span className="text-gray-500 mr-2 mt-1">{isExpanded ? '▼' : '▶'}</span>
                  )}
                  <div className="flex-1">
                    <span className="font-medium text-gray-700">{formatKey(key)}:</span>{' '}
                    {isExpandable && itemCount > 0 && (
                      <span 
                        className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full cursor-pointer hover:bg-blue-200"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (onSelect) {
                            onSelect(newPath, itemCount, true);
                          }
                        }}
                        title="Click to add count as metric"
                      >
                        Count: {itemCount}
                      </span>
                    )}
                    {isExpandable ? (
                      isExpanded ? (
                        <div className="mt-1">
                          {renderValue(val, newPath)}
                        </div>
                      ) : (
                        <span className="text-gray-600">
                          {Array.isArray(val) ? `[${val.length} items]` : '{...}'}
                        </span>
                      )
                    ) : (
                      renderValue(val, newPath)
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      );
    }

    return <span className="text-gray-500">unknown</span>;
  };

  return (
    <div className="font-mono text-sm" onClick={(e) => e.stopPropagation()}>
      {renderValue(data)}
    </div>
  );
}; 